import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, Tag, Folder, Share2, Twitter, Facebook, Linkedin, Link2, Check, BookOpen, ArrowLeft, ArrowRight } from 'lucide-react';
import { getBlogPostBySlug, getRecentPosts, calculateReadingTime, BlogPost } from '../lib/blog';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import { BlogLayout } from '../components/BlogLayout';
import { BlogAcquisitionCTA } from '../components/BlogAcquisitionCTA';
import { BlogStickyPopup } from '../components/BlogStickyPopup';
import '../styles/code-highlight.css';

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [recentPosts, setRecentPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showStickyPopup, setShowStickyPopup] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  
  // Mobile optimization variables removed

  useEffect(() => {
    if (slug) {
      loadPost();
    }
  }, [slug]);

  // Scroll tracking for sticky popup
  useEffect(() => {
    // Don't run if popup is already showing
    if (showStickyPopup) return;

    const checkPopupDismissal = () => {
      const dismissedTimestamp = localStorage.getItem('rulevia_blog_popup_dismissed');
      if (dismissedTimestamp) {
        const dismissedTime = parseInt(dismissedTimestamp);
        const twentyFourHoursAgo = Date.now() - (24 * 60 * 60 * 1000);
        return dismissedTime > twentyFourHoursAgo;
      }
      return false;
    };

    // Check if popup was dismissed recently
    if (checkPopupDismissal()) {
      return;
    }

    const handleScroll = () => {
      // Calculate total page scroll progress
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;

      // Calculate progress as percentage of total scrollable height
      const scrollableHeight = documentHeight - windowHeight;
      const progress = (scrollTop / scrollableHeight) * 100;

      setScrollProgress(progress);

      // Show popup if user has scrolled 10% of the page
      if (progress >= 10 && !showStickyPopup) {
        console.log('Showing popup at', progress, '% scroll');
        setShowStickyPopup(true);
      }
    };

    // Throttle scroll handler for performance
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });

    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [showStickyPopup]);

  const handleClosePopup = () => {
    setShowStickyPopup(false);
  };

  async function loadPost() {
    setLoading(true);
    try {
      const [postData, recentData] = await Promise.all([
        getBlogPostBySlug(slug!),
        getRecentPosts()
      ]);
      
      if (postData) {
        setPost(postData as unknown as BlogPost);
      }
      setRecentPosts((recentData || []).filter((p: any) => p.slug !== slug));
    } catch (error) {
      console.error('Error loading blog post:', error);
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  }

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  function shareOnTwitter() {
    const url = window.location.href;
    const text = post?.title;
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text || '')}`);
  }

  function shareOnFacebook() {
    const url = window.location.href;
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`);
  }

  function shareOnLinkedIn() {
    const url = window.location.href;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`);
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  }

  if (loading) {
    return (
      <BlogLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400"></div>
        </div>
      </BlogLayout>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <>
      <Helmet>
        {/* Page title - meta_title already includes "| Rulevia Blog" suffix */}
        <title>{post.meta_title || `${post.title} | Rulevia Blog`}</title>
        <meta name="description" content={post.meta_description || post.excerpt || ''} />

        {/* Open Graph tags - for Facebook, LinkedIn, etc. */}
        <meta property="og:title" content={post.og_title || post.meta_title || post.title} />
        <meta property="og:description" content={post.og_description || post.meta_description || post.excerpt || ''} />
        <meta property="og:image" content={post.og_image || post.featured_image || 'https://rulevia.com/page_thumbnail.jpg'} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={window.location.href} />

        {/* Twitter Card tags - fallback to Open Graph values */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.og_title || post.meta_title || post.title} />
        <meta name="twitter:description" content={post.og_description || post.meta_description || post.excerpt || ''} />
        <meta name="twitter:image" content={post.og_image || post.featured_image || 'https://rulevia.com/page_thumbnail.jpg'} />
        
        {/* JSON-LD structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.meta_description || post.excerpt || '',
            "image": post.og_image || post.featured_image || 'https://rulevia.com/page_thumbnail.jpg',
            "datePublished": post.published_at,
            "dateModified": post.updated_at,
            "author": {
              "@type": "Person",
              "name": post.author?.name
            },
            "publisher": {
              "@type": "Organization",
              "name": "Rulevia",
              "logo": {
                "@type": "ImageObject",
                "url": "https://rulevia.com/logo.png"
              }
            }
          })}
        </script>
      </Helmet>

      <BlogLayout>
        {/* Back to Blog Bar */}
        <div className="bg-white/5 backdrop-blur-xl border-b border-white/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <button
              onClick={() => navigate('/blog')}
              className="inline-flex items-center text-gray-300 hover:text-white transition-colors group"
            >
              <ArrowLeft className="w-5 h-5 mr-2 transition-transform group-hover:-translate-x-1" />
              Back to Blog
            </button>
          </div>
        </div>

        {/* Article Content */}
        <article className="relative py-8 lg:py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Categories */}
              <div className="flex flex-wrap gap-2 mb-6">
                {post.categories?.map(category => (
                  <span
                    key={category.id}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30"
                  >
                    <Folder className="w-4 h-4 mr-1" />
                    {category.name}
                  </span>
                ))}
              </div>

              {/* Title */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
                <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  {post.title}
                </span>
              </h1>

              {/* Meta Info */}
              <div className="flex flex-wrap items-center text-gray-400 mb-8 gap-4 lg:gap-6">
                <div className="flex items-center">
                  <div className="relative">
                    <img
                      src={post.author?.avatar?.startsWith('http')
                        ? post.author.avatar
                        : '/Logo-checkmark-sidebar.svg'}
                      alt={post.author?.name}
                      className="w-12 h-12 rounded-full border-2 border-purple-500/30 bg-gradient-to-br from-purple-500 to-blue-500 p-1"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/Logo-checkmark-sidebar.svg';
                      }}
                    />
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-gray-900"></div>
                  </div>
                  <div className="ml-3">
                    <p className="font-medium text-white">{post.author?.name}</p>
                    <p className="text-sm text-gray-500">Author</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 mr-2 text-purple-400" />
                  <span>{formatDate(post.published_at!)}</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-blue-400" />
                  <span>{post.reading_time || calculateReadingTime(post.content || '')} min read</span>
                </div>
              </div>

              {/* Featured Image */}
              {post.featured_image && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="relative mb-12 rounded-2xl overflow-hidden shadow-2xl"
                >
                  <img
                    src={post.featured_image}
                    alt={post.title}
                    className="w-full h-[250px] sm:h-[400px] lg:h-[500px] object-cover object-center"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                </motion.div>
              )}

              {/* Content with Enhanced Markdown Rendering */}
              <div className="max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeHighlight, rehypeRaw]}
                  components={{
                    // Headings with proper spacing
                    h1: ({ children }) => (
                      <h1 className="text-4xl font-bold text-white mb-6 mt-8 first:mt-0">
                        {children}
                      </h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-3xl font-bold text-white mb-6 mt-8 first:mt-0">
                        {children}
                      </h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-2xl font-bold text-white mb-4 mt-6">
                        {children}
                      </h3>
                    ),
                    h4: ({ children }) => (
                      <h4 className="text-xl font-semibold text-white mb-4 mt-6">
                        {children}
                      </h4>
                    ),
                    h5: ({ children }) => (
                      <h5 className="text-lg font-semibold text-white mb-3 mt-4">
                        {children}
                      </h5>
                    ),
                    h6: ({ children }) => (
                      <h6 className="text-base font-semibold text-white mb-3 mt-4">
                        {children}
                      </h6>
                    ),

                    // Paragraphs with proper spacing
                    p: ({ children }) => (
                      <p className="text-gray-200 leading-relaxed mb-6 text-lg">
                        {children}
                      </p>
                    ),

                    // Links with hover effects
                    a: ({ href, children }) => (
                      <a
                        href={href}
                        className="text-purple-400 hover:text-purple-300 underline decoration-purple-400/50 hover:decoration-purple-300 transition-colors"
                        target={href?.startsWith('http') ? '_blank' : undefined}
                        rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                      >
                        {children}
                      </a>
                    ),

                    // Strong text
                    strong: ({ children }) => (
                      <strong className="text-white font-semibold">
                        {children}
                      </strong>
                    ),

                    // Emphasis
                    em: ({ children }) => (
                      <em className="text-gray-200 italic">
                        {children}
                      </em>
                    ),

                    // Lists with proper spacing
                    ul: ({ children }) => (
                      <ul className="list-disc list-inside text-gray-200 mb-6 space-y-2">
                        {children}
                      </ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="list-decimal list-inside text-gray-200 mb-6 space-y-2">
                        {children}
                      </ol>
                    ),
                    li: ({ children }) => (
                      <li className="text-gray-200 leading-relaxed">
                        {children}
                      </li>
                    ),

                    // Blockquotes
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-4 border-purple-500 bg-white/5 pl-6 py-4 my-6 rounded-r-lg">
                        <div className="text-gray-200 italic">
                          {children}
                        </div>
                      </blockquote>
                    ),

                    // Code blocks (handled by rehype-highlight)
                    pre: ({ children }) => (
                      <div className="my-6">
                        {children}
                      </div>
                    ),

                    // Inline code
                    code: ({ children, className }) => {
                      const isInline = !className;
                      if (isInline) {
                        return (
                          <code className="bg-white/10 text-purple-300 px-2 py-1 rounded border border-white/20 font-mono text-sm">
                            {children}
                          </code>
                        );
                      }
                      return <code className={className}>{children}</code>;
                    },

                    // Images
                    img: ({ src, alt }) => (
                      <div className="my-8">
                        <img
                          src={src}
                          alt={alt}
                          className="w-full rounded-xl shadow-2xl"
                        />
                        {alt && (
                          <figcaption className="text-center text-gray-400 text-sm mt-3 italic">
                            {alt}
                          </figcaption>
                        )}
                      </div>
                    ),

                    // Tables
                    table: ({ children }) => (
                      <div className="overflow-x-auto my-6">
                        <table className="min-w-full border border-white/20 rounded-lg overflow-hidden">
                          {children}
                        </table>
                      </div>
                    ),
                    thead: ({ children }) => (
                      <thead className="bg-white/10">
                        {children}
                      </thead>
                    ),
                    th: ({ children }) => (
                      <th className="px-4 py-3 text-left text-white font-semibold border-b border-white/20">
                        {children}
                      </th>
                    ),
                    td: ({ children }) => (
                      <td className="px-4 py-3 text-gray-200 border-b border-white/10">
                        {children}
                      </td>
                    ),

                    // Horizontal rule
                    hr: () => (
                      <hr className="border-white/20 my-8" />
                    ),
                  }}
                >
                  {post.content}
                </ReactMarkdown>
              </div>

              {/* Blog Acquisition CTA */}
              <BlogAcquisitionCTA />

              {/* Tags Section */}
              {post.tags && post.tags.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className="mt-12 pt-12 border-t border-white/20"
                >
                  <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
                    <Tag className="w-5 h-5 mr-2 text-blue-400" />
                    Related Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map(tag => (
                      <button
                        key={tag.id}
                        onClick={() => navigate(`/blog?tag=${tag.slug}`)}
                        className="inline-flex items-center px-4 py-2 rounded-full text-sm bg-white/10 text-gray-300 border border-white/20 hover:bg-white/20 hover:text-white transition-all duration-300"
                      >
                        <Tag className="w-3 h-3 mr-1" />
                        {tag.name}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Share Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="mt-12 pt-12 border-t border-white/20"
              >
                <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
                  <Share2 className="w-5 h-5 mr-2 text-purple-400" />
                  Share this article
                </h3>
                <div className="flex flex-wrap gap-4">
                  <button
                    onClick={shareOnTwitter}
                    className="p-4 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-all duration-300 border border-white/20 hover:border-purple-500/30 group"
                    aria-label="Share on Twitter"
                  >
                    <Twitter className="w-5 h-5 text-gray-300 group-hover:text-[#1DA1F2] transition-colors" />
                  </button>
                  <button
                    onClick={shareOnFacebook}
                    className="p-4 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-all duration-300 border border-white/20 hover:border-purple-500/30 group"
                    aria-label="Share on Facebook"
                  >
                    <Facebook className="w-5 h-5 text-gray-300 group-hover:text-[#4267B2] transition-colors" />
                  </button>
                  <button
                    onClick={shareOnLinkedIn}
                    className="p-4 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-all duration-300 border border-white/20 hover:border-purple-500/30 group"
                    aria-label="Share on LinkedIn"
                  >
                    <Linkedin className="w-5 h-5 text-gray-300 group-hover:text-[#0077B5] transition-colors" />
                  </button>
                  <button
                    onClick={copyLink}
                    className="p-4 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-all duration-300 border border-white/20 hover:border-purple-500/30 relative"
                    aria-label="Copy link"
                  >
                    {copied ? (
                      <Check className="w-5 h-5 text-green-400" />
                    ) : (
                      <Link2 className="w-5 h-5 text-gray-300" />
                    )}
                  </button>
                </div>
              </motion.div>

              {/* Related Posts */}
              {recentPosts.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className="mt-16 pt-16 border-t border-white/20"
                >
                  <h3 className="text-2xl font-bold text-white mb-8 flex items-center">
                    <BookOpen className="w-6 h-6 mr-3 text-purple-400" />
                    Related Articles
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {recentPosts.slice(0, 2).map((relatedPost, index) => (
                      <motion.div
                        key={relatedPost.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        viewport={{ once: true }}
                        className="group bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 hover:border-purple-500/30 transition-all duration-300 p-6 hover:bg-white/15"
                      >
                        <h4 className="text-lg font-semibold text-white mb-3 group-hover:text-purple-300 transition-colors">
                          <a
                            href={`/blog/${relatedPost.slug}`}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/blog/${relatedPost.slug}`);
                            }}
                          >
                            {relatedPost.title}
                          </a>
                        </h4>
                        <div className="flex items-center text-sm text-gray-400">
                          <Calendar className="w-4 h-4 mr-2 text-purple-400" />
                          <span>{formatDate(relatedPost.published_at)}</span>
                        </div>
                        <a
                          href={`/blog/${relatedPost.slug}`}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/blog/${relatedPost.slug}`);
                          }}
                          className="inline-flex items-center text-purple-400 hover:text-purple-300 font-medium mt-4 group/link transition-colors"
                        >
                          Read more
                          <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover/link:translate-x-1" />
                        </a>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </motion.div>
          </div>
        </article>
      </BlogLayout>

      {/* Sticky Popup */}
      <BlogStickyPopup
        isVisible={showStickyPopup}
        onClose={handleClosePopup}
      />
    </>
  );
}